const formatProcessType = (type) => {
  if (!type) return 'Process';
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const buildProcessTree = (events) => {
  const processes = new Map();
  const rootProcessIds = new Set();
  const unhandledEvents = [];

  if (!events || events.length === 0) {
    return { tree: [], unhandledEvents: [] };
  }

  for (const event of events) {
    if (!event.process_ids || event.process_ids.length === 0) {
      unhandledEvents.push(event);
      continue;
    }

    let parentId = null;
    for (const processInfo of event.process_ids) {
      const { id, type } = processInfo;

      if (!processes.has(id)) {
        processes.set(id, {
          id,
          type,
          title: formatProcessType(type),
          parentId,
          children: new Set(),
          events: [],
          status: 'processing',
          explicitlyCompleted: false,
          startTime: event.timestamp,
          endTime: null,
        });

        if (parentId) {
          const parentNode = processes.get(parentId);
          if (parentNode) {
            parentNode.children.add(id);
          }
        } else {
          rootProcessIds.add(id);
        }
      }
      parentId = id;
    }
  }

  for (const event of events) {
    if (!event.process_ids || event.process_ids.length === 0) continue;

    const directProcessId = event.process_ids[event.process_ids.length - 1].id;
    const processNode = processes.get(directProcessId);

    if (processNode) {
      processNode.events.push(event);

      if (new Date(event.timestamp) < new Date(processNode.startTime)) {
        processNode.startTime = event.timestamp;
      }

      if (event.msg_type === 'AGENT_COMPLETE') {
        processNode.explicitlyCompleted = true;
        processNode.endTime = event.timestamp;
      }
    }
  }

  const buildNode = (nodeId) => {
    const node = processes.get(nodeId);
    if (!node) return null;

    const children = Array.from(node.children)
      .map(buildNode)
      .filter(Boolean)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    return { ...node, children };
  };

  const tree = Array.from(rootProcessIds)
    .map(buildNode)
    .filter(Boolean)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  const updateStatusesRecursively = (node) => {
    if (!node) return 'complete';

    if (!node.children || node.children.length === 0) {
      node.status = node.explicitlyCompleted ? 'complete' : 'processing';
      return node.status;
    }

    let allChildrenComplete = true;
    for (const child of node.children) {
      const childStatus = updateStatusesRecursively(child);
      if (childStatus !== 'complete') {
        allChildrenComplete = false;
      }
    }

    if (allChildrenComplete) {
      node.status = 'complete';
    } else {
      node.status = 'processing';
    }

    return node.status;
  };

  tree.forEach(updateStatusesRecursively);

  return { tree, unhandledEvents };
};